import { createRouter, createWebHistory, RouteRecordRaw } from "vue-router";
import store from "@/store";
import { Mutations, Actions } from "@/store/enums/StoreEnums";
import JwtService from '@/core/services/JwtService';
import { applySeoMeta, clearVehicleJsonLd } from "@/core/helpers/seoMeta";
import { getMaxiPublicaEncodedId } from '@/core/helpers/maxiVehicleLink';
import { applyPageMetadata, getPublicPageMetadata } from '@/core/helpers/publicPageMetadata';
import { trackPublicNavigation } from '@/core/helpers/publicPageTracking';
const guess = [
  // '/auth',
  '/sign-in',
  '/forgot-password',
]
const clientSide = [
  '/home',
  '/catalogo',
  '/car',
  '/detalle',
  '/precalificacion',
  '/precalificacion-success',
  '/auth-client',
  '/register',
  '/send-verify',
  '/profile',
  '/regain-access',
  '/reset-password-client',
  '/quoter-steps',
]
const routes: Array<RouteRecordRaw> = [
  {
    path: "/admin",
    redirect: "/dashboard",
    component: () => import( "@/layout/Layout.vue"),
    children: [
      {
        path: "/dashboard",
        name: "dashboard",
        component: () => import( "@/views/Dashboard.vue"),
        meta: {
          title: "Dashboard",
          content: "Dashboard"
        }
      },
      {
        path: "/log-book",
        name: "log-book",
        component: () => import( "@/views/modules/admin/LogBook/LogBook.vue"),
        meta: {
          title: "Log Book",
          content: "Log Book"
        }
      },
      {
        path: "/logs",
        name: "logs",
        component: () => import( "@/views/modules/admin/LogBook/Logs.vue"),
        meta: {
          title: "Logs",
          content: "Logs"
        }
      },
      {
        path: "/domains",
        name: "domains",
        component: () => import( "@/views/modules/admin/Domains/DomainList.vue"),
        meta: {
          title: "Domains",
          content: "Domains"
        }
      },
      {
        path: "/agency",
        name: "agency",
        component: () => import( "@/views/modules/admin/Agency/Principal.vue"),
        children: [
          {
            path: '',
            name: "agency-list",
            component: () => import( "@/views/modules/admin/Agency/Agency.vue"),
            meta: {
              title: "Agencias",
              content: "Agencias"
            }
          },
          {
            path: 'detail/:id',
            name: "agency-detail",
            component: () => import( "@/views/modules/admin/Agency/AgencyDetail.vue"),
            meta: {
              title: "Detalle Agencia",
              content: "Detalle Agencia"
            }
          }
        ]
      },
      {
        path: "/vehicle",
        name: "vehicle",
        component: () => import( "@/views/modules/admin/Vehicles/Principal.vue"),
        children: [
          {
            path: '',
            name: "vehicle-list",
            component: () => import( "@/views/modules/admin/Vehicles/Vehicle.vue"),
            meta: {
              title: "Vehiculos",
              content: "Vehiculos"
            }
          },
          {
            path: 'detail/:id/:borrador?',
            name: "vehicle-detail",
            component: () => import( "@/views/modules/admin/Vehicles/VehicleDetail.vue"),
            meta: {
              title: "Detalle Vehiculo",
              content: "Detalle Vehiculo"
            }
          }
        ]
      },
      {
        path: "/vehicle-revision",
        name: "vehicle-revision",
        component: () => import( "@/views/modules/admin/Vehicles/Principal.vue"),
        children: [
          {
            path: '',
            name: "vehicle-list-revision",
            component: () => import( "@/views/modules/admin/Vehicles/Vehicle.vue"),
            meta: {
              title: "Vehiculos",
              content: "Vehiculos"
            }
          },
          {
            path: 'detail/:id/:borrador?',
            name: "vehicle-detail-revision",
            component: () => import( "@/views/modules/admin/Vehicles/VehicleDetail.vue"),
            meta: {
              title: "Detalle Vehiculo",
              content: "Detalle Vehiculo"
            }
          }
        ],
      },
      {
        path: "/vehicle-bitacora",
        name: "vehicle-bitacora",
        component: () => import( "@/views/modules/admin/LogVehicles/Principal.vue"),
        children: [
          {
            path: '',
            name: "vehicle-list-bitacora",
            component: () => import( "@/views/modules/admin/LogVehicles/Vehicle.vue"),
            meta: {
              title: "Bitacora",
              content: "Bitacora"
            }
          }
        ],
      },
      {
        path: "/banners",
        name: "banners",
        component: () => import( "@/views/modules/admin/Banners/Principal.vue"),
        children: [
          {
            path: '',
            name: "option-list-banners",
            component: () => import( "@/views/modules/admin/Banners/Banners.vue"),
            meta: {
              title: "Banners",
              content: "Banners"
            }
          },
          {
            path: ':id',
            name: "banner-type",
            component: () => import( "@/views/modules/admin/Banners/BannerType.vue"),
            meta: {
              title: "Tipo de Banner",
              content: "Tipo de Banner"
            }
          }
        ],
      },
      {
        path: "/group",
        name: "group",
        component: () => import( "@/views/modules/admin/Groups/Principal.vue"),
        children: [
          {
            path: '',
            name: "group-list",
            component: () => import( "@/views/modules/admin/Groups/Group.vue"),
            meta: {
              title: "Grupos",
              content: "Grupos"
            }
          },
          {
            path: 'detail/:id',
            name: "group-detail",
            component: () => import( "@/views/modules/admin/Groups/GroupDetail.vue"),
            meta: {
              title: "Detalle Grupo",
              content: "Detalle Grupo"
            }
          }
        ]
      },
      {
        path: "/role",
        name: "role",
        component: () => import( "@/views/modules/admin/Roles/Principal.vue"),
        children: [
          {
            path: '',
            name: "role-list",
            component: () => import( "@/views/modules/admin/Roles/RolesList.vue"),
            meta: {
              title: "Roles",
              content: "Roles"
            }
          },
          {
            path: 'form/:id?',
            name: "role-form",
            component: () => import( "@/components/modals/forms/Roles/NewRole.vue"),
            meta: {
              title: "Roles",
              content: "Roles"
            }
          }
        ]
      },
      {
        path: "/users",
        name: "users",
        component: () => import( "@/views/modules/admin/Users/Principal.vue"),
        children: [
          {
            path: '',
            name: "user-list",
            component: () => import( "@/views/modules/admin/Users/UsersList.vue"),
            meta: {
              title: "Usuarios",
              content: "Usuarios"
            }
          },
          {
            path: 'detail/:id',
            name: "user-detail",
            component: () => import( "@/views/modules/admin/Users/UserDetail.vue"),
            meta: {
              title: "Detalle Usuario",
              content: "Detalle Usuario"
            }
          }
        ]
      },
      {
        path: "/security-access",
        name: "security-access",
        component: () => import( "@/views/modules/admin/Security/Principal.vue"),
        // async beforeEnter(to, from, next) {
        //   if(await checkIfHavePermissions(to.path)){
        //     next()
        //   }else{
        //     next('/dashboard')
        //   }
        // },
        children: [
          {
            path: '',
            name: "security-principal",
            component: () => import( "@/views/modules/admin/Security/SecurityAndAccess.vue"),
            meta: {
              title: "Seguridad y Acceso",
              content: "Seguridad y Acceso"
            }
          },
          {
            path: 'security-password',
            name: "security-password",
            component: () => import( "@/views/modules/admin/Security/SecurityPasswords.vue"),
            meta: {
              title: "Seguridad y Acceso",
              content: "Seguridad y Acceso"
            }
          },
          {
            path: 'vigency-password',
            name: "vigency-password",
            component: () => import( "@/views/modules/admin/Security/VigencyPassword.vue"),
            meta: {
              title: "Seguridad y Acceso",
              content: "Seguridad y Acceso"
            }
          },

        ]
      },
      {
        path: '/chat',
        name: 'chat',
        component: () => import( "@/views/modules/admin/Chat/HomeView.vue"),
        meta: {
          title: "Chat",
          content: "Chat"
        },
        children: [
          {
            path: ':id(\\d+)',
            component: () => import('@/views/modules/admin/Chat/MessagesView.vue')
          },
          {
            path: ':(.*)',
            component: () => import('@/views/modules/admin/Chat/VoidMessagesView.vue')
          }
        ]
      },
      {
        path: "/leads",
        name: "leads",
        component: () => import( "@/views/modules/admin/Leads/views/LeadsList.vue"),
        meta: {
          title: "Leads",
          content: "Leads"
        },        
      }
    ],
  },
  {
    path: "/auth",
    redirect: "/sign-in",
    component: () => import( "@/components/page-layouts/AuthLayout.vue"),
    children: [
      {
        path: "/sign-in",
        name: "sign-in",
        component: () =>
          import( "@/views/modules/admin/Auth/SignIn.vue"),
          meta: {
            title: "Sign-in",
            content: "Sign-in"
          }
      },
    ]
  },
  {
    path: "/forgot-password",
    name: "forgot-password",
    component: () => import( "@/components/page-layouts/PasswordsLayout.vue"),
    children: [
      {
        path: "",
        component: () =>
          import( "@/views/modules/admin/Auth/ForgotPassword.vue"),
          meta: {
            title: "Forgot-password",
            content: "Forgot-password"
          }
      },
    ]
  },
  {
    path: "/reset-password",
    name: "reset-password",
    component: () => import( "@/components/page-layouts/PasswordsLayout.vue"),
    children: [
      {
        path: "",
        component: () =>
          import( "@/views/modules/admin/Auth/ResetPassword.vue"),
          meta: {
            title: "Reset-password",
            content: "Reset-password"
          }
      },
    ]
  },
  {
    path: "/",
    name: 'client',
    redirect: '/home',
    component: () => import( "@/components/page-layouts/ClientLayout.vue"),
    children: [
      {
        path: "home",
        component: () =>
          import( "@/views/modules/client/Home.vue"),
          meta: {
            title: "Seminuevos Toyota",
            content: "Encuentra seminuevos certificados de distribuidores Toyota. Cotiza y precalifícate en GoKey."
          }
      },
      {
        path: "car/:mpId?",
        name: "car-deeplink",
        redirect: (to) => {
          const encodedVehicleId = getMaxiPublicaEncodedId({
            path: to.path,
            query: to.query as Record<string, unknown>,
            params: to.params as Record<string, unknown>,
          });

          if (encodedVehicleId) {
            return {
              name: 'car-detail',
              params: { id: encodedVehicleId },
              query: {},
            };
          }

          return { path: '/catalogo' };
        },
        meta: {
          title: "Detalle",
          content: "Detalle de vehículo seminuevo en GoKey."
        }
      },
      {
        path: "detalle/:id",
        name: "car-detail",
        component: () =>
          import( "@/views/modules/client/VehicleDetail.vue"),
          meta: {
            title: "Detalle",
            content: "Detalle de vehículo seminuevo en GoKey."
          }
      },
      {
        path: "catalogo",
        name: "catalogo",
        component: () =>
          // Sin webpackPrefetch: competía con LCP del Home (PSI round 3).
          import("@/views/modules/client/VehicleList.vue"),
          meta: {
            title: "Catálogo",
            content: "Catálogo de seminuevos certificados. Filtra por marca, año, precio y más en GoKey."
          }
      },
      {
        path: "auth-client",
        name: "auth-client",
        component: () =>
          import( "@/views/modules/client/auth/Sign-in.vue"),
          meta: {
            title: "Auth",
            content: "Auth"
          }
      },
      {
        path: "register",
        name: "register",
        component: () =>
          import( "@/views/modules/client/auth/Register.vue"),
          meta: {
            title: "Register",
            content: "Register"
          }
      },
      {
        path: "send-verify",
        name: "send-verify",
        component: () =>
          import( "@/views/modules/client/auth/Send-verify.vue"),
          meta: {
            title: "Send-verify",
            content: "Send-verify"
          }
      },
      {
        path: "profile",
        name: "profile",
        component: () =>
          import( "@/views/modules/client/auth/Profile.vue"),
          meta: {
            title: "Profile",
            content: "Profile"
          }
      },
      {
        path: "regain-access",
        name: "forgot-pass",
        component: () =>
          import( "@/views/modules/client/auth/ForgotPass.vue"),
          meta: {
            title: "Forgot-password",
            content: "Forgot-password"
          }
      },
      {
        path: "reset-password-client",
        name: "reset-pass",
        component: () =>
          import( "@/views/modules/client/auth/ResetPass.vue"),
          meta: {
            title: "Reset-password",
            content: "Reset-password"
          }
      }
    ],
  },
  {
    path: "/",
    name: 'precalificacion',
    redirect: '/precalificacion',
    component: () => import( "@/components/page-layouts/PreLayout.vue"),
    children: [
      {
        path: "precalificacion",
        component: () =>
          import( "@/views/modules/client/PreCalification.vue"),
          meta: {
            title: "PreCalificacion",
            content: "PreCalificacion"
          }
      },
      {
        path: "precalificacion-success",
        name: "precalificacion-result",
        component: () =>
          import( "@/views/modules/client/ResultPreCalification.vue"),
          meta: {
            title: "Resultado",
            content: "Resultado"
          }

      }
    ],
  },
  {
    path: "/",
    name: 'quoter',
    redirect: '/quoter-steps',
    component: () => import( "@/components/page-layouts/QuoterLayout.vue"),
    children: [
      {
        path: "/quoter-steps",
        name: "quoter-steps",
        component: () =>
          import( "@/views/modules/client/quoter/views/QuoterSteps.vue"),
          meta: {
            title: "Quoter Steper",
            content: "Quoter Steper"
          }
      }
    ],
  },
  {
    // the 404 route, when none of the above matches
    path: "/404",
    name: "404",
    component: () => import( "@/views/Error404.vue"),
  },
  {
    path: "/:pathMatch(.*)*",
    redirect: "/404",
  },
];

const checkIfHavePermissions = (path) => {
  return new Promise((resolve, reject) => {
    store
    .dispatch(Actions.CHECK_PERMISSIONS, {
      uuid: JwtService.getToken(),
      modulo: path.split('/')[1],
      permiso: "Ver",
      type_user: (JwtService.getTYPE() == 'AGENCIA') ? 'USERS' : 'USER_ENTERPRISE'
    })
    .then(async(response) => {
      if (path === "/agency" && JwtService.getROLE() === "Coordinador de Agencia") {
        const group = await JwtService.getPerm(path, path);
        router.push({name: 'agency-detail', params: { id: group}});
      }
      return resolve(true)
    })
    .catch(() => {
      store
        .dispatch(Actions.LOGOUT)
        .then(() => router.push({ name: "sign-in" }));
      reject(false)
    });
  },
)};

const router = createRouter({
  // History mode (PSI / SEO 4.A). Requiere SPA fallback en CloudFront (403/404 → /index.html).
  history: createWebHistory(process.env.BASE_URL || "/"),
  routes,
});

router.beforeEach(async(to, from, next) => {
  // Nota: se eliminó window.location.reload() forzado en /home|/sign-in|/auth-client.
  // Duplicaba todas las peticiones en la primera visita y anulaba el cache in-memory (2.4).

  // reset config to initial state
  // store.commit(Mutations.RESET_LAYOUT_CONFIG);
  let permission
  // Lado cliente (home/catálogo/…) no usa RBAC admin; evita trabajo extra en cada nav.
  const isClientSideEarly = clientSide.some((item) => to.path.includes(item));
  if(!to.path.includes('dashboard') && !isClientSideEarly){
    permission = await checkIfHavePermissions(to.path);
  }

  // SEO 4.A: title, description, canonical, Open Graph (detalle enriquece luego con JSON-LD)
  if (to.name !== "car-detail") {
    clearVehicleJsonLd();
  }
  applySeoMeta({
    title: String(to.meta.title || "GoKey"),
    description: String(to.meta.content || ""),
    path: to.fullPath,
  });

  await store.dispatch(Actions.VERIFY_AUTH);

  const isGuess = guess.some((item) => to.path.includes(item));
  const isClientSide = clientSide.some((item) => to.path.includes(item));
  

  const auth = !!JwtService.getToken()
  
  const verified =  !!JwtService.getVERIFIED();
  
  const redirect = '/sign-in'
  //console.log(permission, isGuess, isClientSide, auth)
  if (isGuess) {
    
    if(auth){
      if(!verified){
        
        //console.log('verificar')
        next('/reset-password')
      }else{
        next('/dashboard')
      }
    }else{
      next();
    }
  }
  else if(isClientSide){
    next();
  }
  else if (auth) {
    if(!verified && to.path != '/reset-password'){
      
      next()
    }else if(verified && to.path == '/reset-password'){
      next('/dashboard')
    } else{
      if(to.path == '/dashboard') {
        permission = true
        
      }

      if(to.path == '/reset-password') {
        permission = true
       
      }

      if(permission){
        
        next();
      }else{
        next('/dashboard')
      }
    }
  } else {
    next(redirect)
  }
  // Scroll page to top on every route change
  if(!to.path.includes('/catalogo')){
    setTimeout(() => {
      window.scrollTo(0, 0);
    }, 100);
  }
});


router.afterEach((to, from, failure) => {
  if (failure) return;
  // A query-only change must not replace the loaded vehicle title with its fallback.
  if (!(to.name === 'car-detail' && from.name === 'car-detail' && to.params.id === from.params.id)) {
    const metadata = getPublicPageMetadata(to);
    if (metadata) applyPageMetadata(metadata);
  }
  trackPublicNavigation(to);
});

export default router;
